import React from "react";
import { Navigate, useLocation, Link } from "react-router-dom";
import { Loader2, ShieldAlert } from "lucide-react";
import { useCSuiteAuth } from "../../state/CSuiteAuthContext";

/**
 * CSuiteProtected — three walls:
 *   1. Must be logged in.
 *   2. Role must be CIO / CFO / ADMIN — unless the org has explicitly opted
 *      OPERATORs into C-Suite access via `share_csuite_with_operators`.
 *   3. Subscription wall is enforced at each backend endpoint (402 → paywall).
 */
export function CSuiteProtected({ children }) {
  const { user, org, loading } = useCSuiteAuth();
  const loc = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-[var(--paper)]">
        <Loader2 size={32} className="animate-spin text-[var(--navy)]" />
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/csuite" replace state={{ from: loc.pathname }} />;
  }

  // Role wall — OPERATOR is blocked unless org has opted them in.
  const role = user.role;
  const opted = !!(org && org.share_csuite_with_operators);
  if (role === "OPERATOR" && !opted) {
    return (
      <div className="min-h-screen grid place-items-center bg-[var(--paper)] px-6" data-testid="csuite-role-gate">
        <div className="max-w-lg w-full p-8 rounded-md border" style={{ background: "#FFFFFF", borderColor: "#F0D3D8" }}>
          <span className="w-12 h-12 rounded-sm grid place-items-center text-white mb-4"
                style={{ background: "linear-gradient(135deg, #7A1F2B 0%, #B03A48 100%)" }}>
            <ShieldAlert size={22} />
          </span>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#7A1F2B]">
            C-Suite Executive Portal · Role-restricted
          </div>
          <h1 className="font-display text-2xl md:text-3xl font-extrabold text-[#0F1723] mt-2 leading-tight">
            The C-Suite portal is for CIO and CFO roles only.
          </h1>
          <p className="text-sm text-zinc-700 mt-3 leading-relaxed">
            Your account is registered as an <b>Operator</b>. That role manages projects, RAID logs and schedules
            inside the Basic Portal — the C-Suite view is a separate, boardroom-only workspace.
          </p>
          <p className="text-sm text-zinc-700 mt-3 leading-relaxed">
            If you need visibility into your organization's rollup, ask an <b>Owner or Admin</b> to enable
            <em> Operator access</em> under <span className="font-mono text-[12px]">Admin → C-Suite Access</span>.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link to="/projects" className="btn btn-primary" data-testid="btn-goto-basic">
              Open the Basic Portal
            </Link>
            <Link to="/" className="btn btn-ghost" data-testid="btn-goto-home">
              Back to homepage
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return children;
}
